<template>
  <div>
    <div class="wise-promotion-banner"></div>
    <div class="payments">
      <section class="pay-periods-section">
        <h4 class="mt-0 mb-0">Pay periods</h4>
        <p class="text-grey mb-24">Set how worked hours are grouped into timesheets. Timesheets will follow configured pay periods, if generated.</p>

        <template v-if="!hasCustomPayPeriodDates">
          <div class="row">
            <div class="col-xs-12 col-md-6">
              <div
                id="pay-period"
                class="form-group"
              >
                <ValidationProvider>
                  <label class="control-label">Frequency</label>
                  <p class="field-hint text-grey mb-10">How often timesheets reset.</p>
                  <AppSelect
                    v-model="payPeriod"
                    :options="payPeriodOptions"
                    :select2-options="defaultSelect2Options"
                    :disabled="payPeriodIsExternal"
                    @input="onPayPeriodSelect"
                  />
                </ValidationProvider>
              </div>
            </div>
            <div
              v-if="showPeriodStartOptions"
              class="col-xs-12 col-md-6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                name="startOn"
                mode="eager"
                rules="required"
              >
                <div
                  id="start-on"
                  class="form-group"
                  :class="{ 'has-error': errors[0] }"
                >
                  <label class="control-label">Period start</label>
                  <p class="field-hint text-grey mb-10">The day each timesheet period begins.</p>
                  <div class="d-flex flex-column flex-sm-row period-start-options flex-wrap">
                    <span
                      v-for="(date, index) of startDates"
                      :key="index"
                    >
                      <label
                        :for="`period-start-${index}`"
                        class="period-start-option label label-lg label-rounded a-tooltip mr-5 mb-10"
                        :class="date.value === periodStart ? 'label-primary period-start-selected' : 'label-gray-light'"
                      >
                        {{ formatPeriodDate(date.value) }}
                      </label>
                      <input
                        :id="`period-start-${index}`"
                        v-model="periodStart"
                        type="radio"
                        name="period-start"
                        :value="date.value"
                      />
                    </span>
                  </div>
                  <span class="help-block">{{ errors[0] }}</span>
                </div>
              </ValidationProvider>
            </div>
          </div>
        </template>

        <PayPeriodCustomDatesSelector
          v-else
          :value="customPayPeriodSelectorValue"
          :initial-state="customPayPeriodSelectorInitialState"
          :pay-period-options="customPayPeriodOptions"
          :pay-period-select-disabled="payPeriodIsExternal"
          :organization-timezone="organizationTimeZone?.for"
          :member-id="membership?.user?.id"
          surface="pay_bill"
          frequency-hint="How often timesheets reset."
          weekly-hint="The day each timesheet period begins."
          monthly-hint="The day each timesheet period begins."
          twice-per-month-hint="The dates each timesheet period begins."
          @input="onCustomPayPeriodSelectorInput"
        />

        <div
          v-if="payPeriodInfoBanner"
          class="alert alert-info pay-period-info-banner mt-15 mb-0"
          role="status"
        >
          <i class="hi hi-info-filled pay-period-info-banner-icon mr-5" />
          Once saved, timesheets will run <strong>{{ payPeriodInfoBanner.frequency }}</strong>.
          First period begins on <strong>{{ payPeriodInfoBanner.firstPeriodDate }}</strong>.
        </div>

        <div class="d-flex flex-column flex-sm-row mt-15 gap-40">
          <div v-if="canEditTimesheets">
            <label class="control-label">Past timesheets</label>
            <div>
              <button
                class="btn btn-transparent-outline edit-timesheets-btn"
                data-testid="edit-timesheets-btn"
                @click="toggleAdjustTimesheetsDialog(true)"
              >
                Edit timesheets
              </button>
            </div>
          </div>
          <div v-if="canApproveTimesheets">
            <label class="control-label">Timesheet generation & approval</label>
            <div
              id="timesheets-enabled"
              class="form-group mt-10 mb-0"
            >
              <ValidationProvider>
                <div class="checkbox">
                  <AppOnOffToggle
                    v-model="timesheetsEnabled"
                    data-testid="pay-bill-require-timesheet-approval-toggle"
                    :disabled="timesheetToggleDisabled"
                  >
                    <span>{{ timesheetsEnabled ? 'Enabled' : 'Disabled' }}</span>
                  </AppOnOffToggle>
                </div>
              </ValidationProvider>
            </div>
          </div>
        </div>
        <AppAlert
          v-if="showTimesheetsDisabledWarning"
          type="danger"
          class="mt-10 mb-0 bg-white d-inline-flex py-5"
        >
          <i class="hi hi-16 hi-warning" />
          <span class="text-default">This will delete any open, submitted, and denied timesheets and will stop generating future timesheets for this user. Approved or paid timesheets will not be affected.</span>
        </AppAlert>
        <AppAlert
          v-if="showTimesheetsEnabledWarning"
          type="info"
          class="mt-10 mb-0 bg-white d-inline-flex py-5"
        >
          <i class="hi hi-16 hi-info-filled hi-primary" />
          <span class="text-default">This will start generating future timesheets for this user, which will need to be approved.</span>
        </AppAlert>
      </section>

      <section class="member-rates-section pay-details-row mb-15">
        <h4 class="mt-32 mb-0">Member Rates</h4>
        <p class="text-grey mb-24">Manage this member's pay, bill, and project rates.</p>

        <div class="row">
          <div class="col-sm-12">
            <div id="payment-rates">
              <ValidationProvider ref="payment_rates">
                <AppToggleButtonGroup
                  :value="paymentSection"
                  :label="toggleButtonTooltipLabel"
                  :label-tooltip="toggleButtonTooltipText"
                  :tooltip-options="toggleButtonTooltipOptions"
                  :items="paymentRateItems"
                  :selected="paymentSection"
                  @input="togglePaymentSection"
                />
              </ValidationProvider>
            </div>
          </div>
        </div>
      </section>

      <!--
        v-show keeps both instances mounted so VeeValidate tracks dirty state across tab switches,
        triggering the unsaved changes guard on page leave. v-if would destroy the hidden instance
        and lose dirty state, requiring a manual setFlags({ dirty: true }) on every toggle.
      -->
      <MembersRatesFields
        v-show="isPayRateSection"
        type="pay"
        @reset-rates="resetRateFields"
      />
      <MembersRatesFields
        v-show="isBillRateSection"
        type="bill"
        @reset-rates="resetRateFields"
      />

      <MembersProjectRatesTable
        v-if="isProjectRateSection"
        :saved-pay-rate="initialPayRate"
        :saved-bill-rate="initialBillRate"
      />

      <slot name="actions" />
      <AppDialog
        v-if="canEditTimesheets && showAdjustTimesheetsDialog"
        show
        data-testid="adjust-timesheets-dialog"
        title="Edit past timesheets"
        size="large"
        append-to-body
        @close="toggleAdjustTimesheetsDialog(false)"
      >
        <AdjustTimesheetsForm
          v-bind="adjustTimesheetsFormProps"
          @close="toggleAdjustTimesheetsDialog(false)"
        />
      </AppDialog>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import PayPeriodCustomDatesSelector from '~/components/shared/PayPeriodCustomDatesSelector.vue';
import { mapFormFields } from '~/utils/form_utils';
import AdjustTimesheetsForm from './AdjustTimesheetsForm.vue';
import MembersProjectRatesTable from './MembersProjectRatesTable.vue';
import MembersRatesFields from './MembersRatesFields.vue';

export default {
  name: 'MemberDialogTabPayments',
  components: { AdjustTimesheetsForm, MembersProjectRatesTable, MembersRatesFields, PayPeriodCustomDatesSelector },
  data: () => ({
    defaultSelect2Options: {
      allowClear: false,
      multiple: false,
      width: '100%',
    },
    toggleButtonTooltipLabel: 'Type',
    toggleButtonTooltipText:
      '<p><b>Pay rate</b> is the one you pay to this member directly.</p>' +
      '<p><b>Bill rate</b> is to charge your customers for this member.</p>' +
      '<p><b>Project rate</b> is a per-project override of the pay and bill rates.</p>',
    toggleButtonTooltipOptions: { html: true },
    initialBillRate: '',
    initialPayRate: '',
    initialPayPeriod: '',
    previousPayPeriod: null,
    initialPeriodWeekDay: null,
    initialPeriodStartDay: null,
    initialPeriodSecondStartDay: null,
    initialPeriodAnchorDate: null,
    initialTwicePerMonthHasSelection: false,
    initialTimesheetsEnabled: false,
    showAdjustTimesheetsDialog: false,
    paymentRateItems: Object.freeze([
      { id: 0, value: 0, key: 'pay_rates', text: 'Pay rate' },
      { id: 1, value: 1, key: 'bill_rates', text: 'Bill rate' },
      { id: 2, value: 2, key: 'project_rates', text: 'Project rate' },
    ]),
  }),
  computed: {
    ...mapState(['membership', 'organization']),
    ...mapGetters([
      'canApproveTimesheets',
      'payPeriodIsNone',
      'payPeriodIsExternal',
      'payPeriodOptions',
      'hasPayPeriod',
      'paymentSection',
      'periodStartDates',
      'organizationTimeZone',
      'hasCustomPayPeriodDates',
    ]),
    ...mapFormFields('membership', {
      periodStart: 'payments.period_start',
      payPeriod: 'payments.pay_period',
      timesheetsEnabled: 'payments.timesheets_enabled',
      billRate: 'payments.bill_rate',
      payRate: 'payments.pay_rate',
      billRateNote: 'payments.bill_rate_note',
      payRateNote: 'payments.pay_rate_note',
      periodWeekDay: 'payments.period_week_day',
      periodStartDay: 'payments.period_start_day',
      periodSecondStartDay: 'payments.period_second_start_day',
      periodAnchorDate: 'payments.period_anchor_date',
    }),
    isPayRateSection() {
      return this.paymentSection === 0;
    },
    isBillRateSection() {
      return this.paymentSection === 1;
    },
    isProjectRateSection() {
      return this.paymentSection === 2;
    },
    timesheetToggleDisabled() {
      let disabled = !this.hasPayPeriod;

      if (this.payPeriodIsNone) {
        disabled = true;
      } else if (!this.payPeriodIsExternal) {
        disabled = false;
      }

      return disabled;
    },
    showTimesheetsDisabledWarning() {
      return this.initialTimesheetsEnabled && !this.timesheetsEnabled && !this.timesheetToggleDisabled;
    },
    showTimesheetsEnabledWarning() {
      return !this.initialTimesheetsEnabled && this.timesheetsEnabled && !this.timesheetToggleDisabled;
    },
    showPeriodStartOptions() {
      return !this.payPeriodIsNone && !this.payPeriodIsExternal;
    },
    payPeriodInfoBanner() {
      if (this.payPeriodIsNone || this.payPeriodIsExternal) { return null; }
      // Custom-pay-period orgs get a banner from PayPeriodCustomDatesSelector itself
      if (this.hasCustomPayPeriodDates) { return null; }
      if (!this.periodStart) { return null; }

      const option = this.payPeriodOptions.find((o) => o.id === this.payPeriod);

      if (!option) { return null; }

      const startMoment = moment(this.periodStart);

      if (!startMoment.isValid()) { return null; }

      return {
        frequency: option.text.toLowerCase(),
        firstPeriodDate: startMoment.format('MMM D, YYYY'),
      };
    },
    customPayPeriodOptions() {
      return this.payPeriodOptions.map((option) => ({ id: option.id, text: option.text }));
    },
    customPayPeriodSelectorValue() {
      return {
        payPeriodId: this.payPeriod,
        previousPayPeriodId: this.previousPayPeriod,
        periodWeekDay: this.periodWeekDay,
        periodStartDay: this.periodStartDay,
        periodSecondStartDay: this.periodSecondStartDay,
        periodAnchorDate: this.periodAnchorDate,
      };
    },
    customPayPeriodSelectorInitialState() {
      return {
        initial_pay_period: this.initialPayPeriod,
        initial_period_week_day: this.initialPeriodWeekDay,
        initial_period_start_day: this.initialPeriodStartDay,
        initial_period_second_start_day: this.initialPeriodSecondStartDay,
        initial_period_anchor_date: this.initialPeriodAnchorDate,
        initial_twice_per_month_has_selection: this.initialTwicePerMonthHasSelection,
      };
    },
    startDates() {
      const period = this.payPeriodOptions.find((period) => period.id === this.payPeriod);

      return (
        period?.key &&
        this.periodStartDates &&
        this.periodStartDates[period.key]?.map((date) => ({
          value: date,
          text: this.formatPeriodDate(date),
        }))
      );
    },
    canEditTimesheets() {
      return this.membership.timesheets_info?.can_edit;
    },
    adjustTimesheetsFormProps() {
      const minDateFrom = this.membership.timesheets_info?.allow_adjust_since;
      const payPeriodKey = this.membership.payments.pay_period;

      return {
        organizationId: this.organization?.id,
        userId: this.membership.user?.id,
        minDateFrom: minDateFrom ? moment(minDateFrom) : moment().subtract(3, 'months'),
        maxDateTo: moment().date() < 15 ? moment().endOf('month') : moment().add(1, 'month').date(15).startOf('day'),
        allowAdjustTimesheets: true,
        initialPayPeriod: this.payPeriodOptions.find((period) => period.id === payPeriodKey)?.key || 'weekly',
        initialDateFrom: moment(this.membership.timesheets_info?.default_adjust_since),
      };
    },
  },
  created() {
    // Not the best solution, but all the data is in the store, and we need to reset it here
    this.setInitialValues();
  },
  methods: {
    ...mapActions(['updatePaymentSection']),
    onPayPeriodSelect() {
      if (this.canApproveTimesheets && this.payPeriodIsNone) {
        this.timesheetsEnabled = false;
      }
      this.periodStart = this.startDates?.[0]?.value;
    },
    onCustomPayPeriodSelectorInput(nextState) {
      this.payPeriod = nextState.payPeriodId;
      this.previousPayPeriod = nextState.previousPayPeriodId;
      this.periodWeekDay = nextState.periodWeekDay;
      this.periodStartDay = nextState.periodStartDay;
      this.periodSecondStartDay = nextState.periodSecondStartDay;
      this.periodAnchorDate = nextState.periodAnchorDate;

      if (this.canApproveTimesheets && this.payPeriodIsNone) {
        this.timesheetsEnabled = false;
      }
    },
    formatPeriodDate(date) {
      return moment(date).format('ddd, D MMM, YYYY');
    },
    togglePaymentSection(value) {
      this.updatePaymentSection(value);
      const label = this.paymentRateItems.find((i) => i.value === value)?.text ?? 'Unknown';

      window.analytics?.track('Member rate toggle clicked', { clicked: label });
    },
    setInitialValues() {
      this.initialBillRate = this.billRate;
      this.initialPayRate = this.payRate;
      this.initialPayPeriod = this.payPeriod ?? '';
      this.previousPayPeriod = this.payPeriod ?? '';
      this.initialPeriodWeekDay = this.periodWeekDay ?? null;
      this.initialPeriodStartDay = this.periodStartDay ?? null;
      this.initialPeriodSecondStartDay = this.periodSecondStartDay ?? null;
      this.initialPeriodAnchorDate = this.periodAnchorDate ?? null;
      this.initialTwicePerMonthHasSelection =
        this.payPeriod === '1' && this.periodStartDay != null && this.periodSecondStartDay != null;
      this.initialTimesheetsEnabled = this.timesheetsEnabled;
    },
    resetRateFields() {
      this.payRate = this.initialPayRate;
      this.billRate = this.initialBillRate;
      this.payRateNote = '';
      this.billRateNote = '';
    },
    toggleAdjustTimesheetsDialog(flag) {
      this.showAdjustTimesheetsDialog = flag;
    },
  },
};
</script>
